import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import { Timestamp, doc, setDoc } from 'firebase/firestore';
import Confetti from 'react-confetti';
import { FaEnvelope, FaGift, FaTrophy } from 'react-icons/fa';
import { db } from '~/config/firebase.config';
import { CouponDoc } from '~/services/db/coupons/CouponsAPI';
import { EmailAPI } from '~/services/email/EmailAPI';
import { CODE_CLIENT_ID, CODE_LANG } from '~/config/endpoints.config';
import { generateCouponToken } from '~/utils/couponToken';
import { Input } from '~/components/ui/input';
import { PrivacyNotice } from '~/components/PrivacyNotice';
import styles from './CouponWonFirstScreen.module.css';

type Props = {
    scopeLabel: string;
    coupon: CouponDoc;
    clientID: string;
    onViewReward: () => void;
    onBack: () => void;
};

export function CouponWonFirstScreen({ scopeLabel, coupon, clientID, onViewReward, onBack }: Props) {
    const { t, i18n } = useTranslation();
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [showConfetti, setShowConfetti] = useState(true);
    const [userData, setUserData] = useLocalStorage<any>('userData', {});
    const [email, setEmail] = useState<string>(userData?.email || '');
    const [emailError, setEmailError] = useState<string>('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    useEffect(() => {
        const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setShowConfetti(false), 5000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (userData?.email) {
            setEmail(userData.email);
        }
    }, [userData?.email]);

    const validateEmail = (value: string): string => {
        if (value && !/\S+@\S+\.\S+/.test(value)) {
            return t('coupon.won.emailInvalid');
        }
        return '';
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setEmail(value);
        if (value) {
            setEmailError(validateEmail(value));
        } else {
            setEmailError('');
        }
    };

    const handleViewRewardClick = async () => {
        const error = validateEmail(email);
        if (error) {
            setEmailError(error);
            return;
        }

        setIsSendingEmail(true);

        const rewardWindow = window.open('about:blank', '_blank');
        const windowOpened = !!rewardWindow;

        let emailSent = false;
        let rewardUrl = '';

        try {
            const trimmedEmail = email.trim();

            if (trimmedEmail && trimmedEmail !== userData?.email && clientID && userData?.uid) {
                try {
                    const updatedUserData = { ...userData, email: trimmedEmail };
                    setUserData(updatedUserData);

                    const userDocRef = doc(db, 'clients', clientID, 'userData', userData.uid);
                    // Le localStorage est commun à tous les établissements : ses
                    // autres champs (nom, téléphone, consentement newsletter…) ont
                    // pu être donnés à un AUTRE lieu. On n'envoie à celui-ci que
                    // l'identité de jeu et l'email saisi ici ; la fusion garde le reste.
                    // (Firestore refuse `undefined` : on ne garde que les champs présents.)
                    const record = Object.fromEntries(
                        Object.entries({
                            uid: userData.uid,
                            username: userData.username,
                            avatar: userData.avatar,
                            email: trimmedEmail,
                        }).filter(([, value]) => value !== undefined)
                    );
                    await setDoc(userDocRef, { ...record, date: Timestamp.now() }, { merge: true });
                } catch (saveError) {
                    console.error('Error saving email:', saveError);
                }
            }

            const lang = i18n.language || 'en';
            const accessToken = await generateCouponToken(coupon.playerId, coupon.id, clientID);
            const baseRewardUrl = `${window.location.origin}/reward/${coupon.id}?${CODE_CLIENT_ID}=${clientID}&${CODE_LANG}=${lang}&token=${accessToken}`;

            if (trimmedEmail && /\S+@\S+\.\S+/.test(trimmedEmail)) {
                try {
                    const emailResult = await EmailAPI.sendCouponEmail({
                        email: trimmedEmail,
                        couponId: coupon.id,
                        offerName: coupon.offerName,
                        rewardUrl: baseRewardUrl,
                        playerName: userData?.username,
                        language: i18n.language || 'en',
                        triggerName: scopeLabel,
                    });

                    if (emailResult.success) {
                        emailSent = true;
                    } else {
                        console.error('Failed to send email:', emailResult.message);
                    }
                } catch (emailError) {
                    console.error('Error sending email:', emailError);
                }
            }

            rewardUrl = `${baseRewardUrl}${emailSent ? '&emailSent=true' : ''}`;

            if (windowOpened && rewardWindow) {
                rewardWindow.location.href = rewardUrl;
            } else {
                window.location.href = rewardUrl;
            }
        } catch (handleError) {
            console.error('Error in handleViewRewardClick:', handleError);
            const lang = i18n.language || 'en';
            const accessToken = await generateCouponToken(coupon.playerId, coupon.id, clientID);
            rewardUrl = `${window.location.origin}/reward/${coupon.id}?${CODE_CLIENT_ID}=${clientID}&${CODE_LANG}=${lang}&token=${accessToken}`;
            if (windowOpened && rewardWindow) {
                rewardWindow.location.href = rewardUrl;
            } else {
                window.location.href = rewardUrl;
            }
        } finally {
            setIsSendingEmail(false);
        }

        onViewReward();
    };

    const isEmailValid = email.trim() === '' || !emailError;

    return (
        <div className={styles.content}>
            {showConfetti && (
                <Confetti
                    width={windowSize.width}
                    height={windowSize.height}
                    recycle={false}
                    numberOfPieces={300}
                    gravity={0.3}
                    initialVelocityY={20}
                    initialVelocityX={10}
                    colors={['#FFD700', '#87CEEB', '#FF69B4', '#FF6B6B', '#32CD32', '#FFA500', '#9370DB']}
                    style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
                />
            )}
            <div className={styles.trophyContainer}>
                <FaTrophy className={styles.trophy} />
            </div>
            <h1 className={styles.title}>{t('coupon.won.title')}</h1>
            <p className={styles.subtitle}>{t('coupon.won.subtitle')}</p>
            <div className={styles.recordBanner}>
                <FaTrophy className={styles.recordIcon} />
                <span>{scopeLabel}</span>
            </div>
            <div className={styles.emailInputContainer}>
                <Input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder={t('coupon.won.emailPlaceholder')}
                    error={!!emailError}
                    helper={emailError}
                    containerClassName={styles.emailInput}
                    icon={<FaEnvelope size={20} />}
                >
                    {t('coupon.won.emailLabel')}
                </Input>
                <PrivacyNotice className={styles.privacy} />
            </div>
            <button
                className={styles.rewardButton}
                onClick={handleViewRewardClick}
                type="button"
                disabled={!isEmailValid || isSendingEmail}
            >
                <FaGift className={styles.giftIcon} />
                <span>{isSendingEmail ? t('coupon.won.sending') : t('coupon.won.viewReward')}</span>
            </button>
            <button className={styles.backButton} onClick={onBack} type="button">
                {t('coupon.won.backToGame')}
            </button>
        </div>
    );
}

export default CouponWonFirstScreen;
